import { useRef } from "react";
import { X } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle } from "@libs/zapi-ui/shadcn";
import { AppButton, AppInput, FigmaTypography, isEnterSubmit } from "@libs/zapi-ui/custom";
import { InnoveraIcon } from "@libs/zapi-ui/assets";
import { useTranslation } from "react-i18next";

type TProps = {
  isOpen: boolean;
  value: string;
  isLoading: boolean;
  projectName?: string;
  projectLogoUrl?: string | null;
  onChange: (value: string) => void;
  onCancel: () => void;
  onSave: () => void;
};

export function RenameVersionModal({
  isOpen,
  value,
  isLoading,
  projectName,
  projectLogoUrl,
  onChange,
  onCancel,
  onSave
}: TProps) {
  const { t } = useTranslation("common");
  const fieldRef = useRef<HTMLDivElement>(null);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        data-testid="rename-version-modal"
        showCloseButton={false}
        aria-describedby={undefined}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          fieldRef.current?.querySelector("input")?.focus();
        }}
        className="w-[480px] max-w-[480px] gap-0 overflow-hidden rounded-xl border-0 p-0 shadow-300 ring-1 ring-inset ring-innoveraZinc-150 sm:rounded-xl"
      >
        <div className="relative flex h-[92px] shrink-0 items-center gap-4 p-5">
          <span className="flex size-[52px] shrink-0 items-center justify-center overflow-hidden rounded-lg bg-innoveraZinc-100">
            {projectLogoUrl ? (
              <img src={projectLogoUrl} alt="" className="size-full object-contain p-2.5" />
            ) : (
              <InnoveraIcon className="size-6 text-innoveraZinc-400" />
            )}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <DialogTitle asChild>
              <FigmaTypography component="span" variant="heading-3" color="zinc900" className="min-w-0 truncate">
                {projectName ?? t("versions.renameTitle")}
              </FigmaTypography>
            </DialogTitle>
            <FigmaTypography component="span" variant="body-2" color="zinc500">
              {t("versions.renameTitle")}
            </FigmaTypography>
          </span>
          <button
            type="button"
            aria-label={t("versions.close")}
            onClick={onCancel}
            className="absolute right-5 top-9 flex size-5 items-center justify-center text-innoveraZinc-500 hover:text-innoveraZinc-900"
          >
            <X size={20} />
          </button>
        </div>

        <div ref={fieldRef} className="flex flex-col gap-2.5 px-5">
          <FigmaTypography component="span" variant="label-2" color="zinc900" className="flex h-[10px] items-center">
            {t("versions.nameLabel")}
          </FigmaTypography>
          <AppInput
            value={value}
            disabled={isLoading}
            className="h-9"
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (isEnterSubmit(e, { allowShift: true })) {
                e.preventDefault();
                onSave();
              }
            }}
          />
        </div>

        <div className="flex h-[72px] shrink-0 items-center justify-end gap-2 p-5">
          <AppButton type="button" appearance="outlined" color="zinc150" disabled={isLoading} onClick={onCancel}>
            {t("cancel")}
          </AppButton>
          <AppButton type="button" color="zinc950" isLoading={isLoading} onClick={onSave}>
            {t("versions.save")}
          </AppButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
